'use strict';

import Lottie from 'lottie-web/build/player/lottie_light.min';
const redSpinnerData = require('./spinners/spinner_animation_min.json');

/**
 * @class VZSpinner
 * @param {HTMLElement} [parent] - Parent element for the spinner to be added in.
 * @description
 * Creates a VZ-Spinner component
 *
 * @example
 * <vz-spinner>
 *   <div class="veil">
 *     <div class="underlay"></div>
 *     <div class="spinner"></div>
 *     <div class="sr-only js-loading-message" role="status"></div>
 *   </div>
 * </vz-spinner>
 */
class VZSpinner extends HTMLElement {
    constructor(parentEl) {
        super();

        this.showing = false;
        this.stop = this.stop.bind(this);
        this.veil = null;

        // Screen readers: Alert data
        this.bodyElement = null;
        this.loadingMessageStart = null;
        this.loadingMessageStop = null;
        this.loadingMessageContainer = null;

        const parentElement = parentEl || document.body;
        const location = parentElement.tagName === 'IMG' ? parentElement.parentElement : parentElement;
        // This ensures: existing callers using "new VZSpinner(parent)" still work
        if (!this.parentNode) {
            location.appendChild(this);
        }
    }

    /**
     * @public
     * @param {string} [msg] - A message to be shown below the spinner.
     * @returns {Promise} promise
     */
    start(msg) {
        return new Promise((resolve) => {
            if (this.showing) resolve();
            const parentPosition = window.getComputedStyle(this.parentElement).getPropertyValue('position');

            if (parentPosition === 'static') {
                if (this.parentElement.tagName !== 'BODY') {
                    this.parentElement.style.position = 'relative';
                }
                this.parentElement.classList.add('veiled');
            }

            if (msg && this.veil) {
                this.veil.insertAdjacentHTML('beforeend', `<div class="content">${msg}</div>`);
            }

            this.showing = true;
            setTimeout(() => {
                if (this.showing) {
                    this.lottie?.play();
                    this.veil?.classList.add('show');

                    // Screen readers: Notify user that content is busy loading
                    if (this.bodyElement) {
                        this.loadingMessageContainer.textContent = this.loadingMessageStart;
                        this.bodyElement.ariaBusy = true;
                    }
                }
                resolve();
            }, 500);
        });
    }

    /**
     * @public
     * @returns {Promise} promise
     */
    stop() {
        return new Promise((resolve) => {
            if (!this.showing) resolve();
            if (this.parentElement.classList.contains('veiled')) {
                this.parentElement.style.position = '';
                this.parentElement.classList.remove('veiled');
            }

            const content = this.veil.querySelector('.content');
            if (content) content.remove();
            this.veil?.classList.remove('show');
            this.showing = false;
            this.lottie?.stop();

            // Screen readers: Notify user that content finished loading
            if (this.bodyElement) {
                this.loadingMessageContainer.textContent = this.loadingMessageStop;
                this.bodyElement.ariaBusy = false;
            }

            resolve();
        });
    }

    connectedCallback() {
        // Prevent re-initialization if moved in DOM
        if (this.veil) return;

        this.innerHTML = `
            <div class="veil">
                <div class="underlay"></div>
                <div class="spinner"></div>
                <div class="sr-only js-loading-message" role="status"></div>
            </div>
        `;

        // Screen readers: Prepare for live content updates
        this.bodyElement = document.querySelector('body[aria-busy]');
        if (this.bodyElement) {
            this.loadingMessageStart = this.bodyElement.getAttribute('data-spinner-start');
            this.loadingMessageStop = this.bodyElement.getAttribute('data-spinner-stop');
            this.loadingMessageContainer = this.querySelector('.js-loading-message');
        }

        this.veil = this.querySelector('.veil');
        this.veil.addEventListener('click', (evt) => evt.stopPropagation());

        // Initialize Lottie only after DOM is ready
        this.lottie = Lottie.loadAnimation({
            container: this.veil.querySelector('.spinner'),
            renderer: 'svg',
            loop: true,
            autoplay: false,
            animationData: redSpinnerData
        });
    }
}

window.VZSpinner = window.customElements.get('vz-spinner') || VZSpinner;
export default window.VZSpinner;

if (!window.customElements.get('vz-spinner')) {
    window.customElements.define('vz-spinner', VZSpinner);
}
